import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Flash
} from "@zentrani/ui";
import { useSession } from "../state/session";
import {
  enablePushNotifications,
  hasActivePushSubscription,
  iosNeedsInstall,
  pushSupported,
} from "../lib/push";

const DISMISS_KEY = "zentrani.push_prompt_dismissed";

/**
 * One-shot prompt for residents to enable Web Push after login.
 * Skips when permission is denied, already subscribed, or previously dismissed.
 */
export function PushOptInBanner() {
  const { session } = useSession();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!session || session.role !== "resident") return;
    if (typeof window === "undefined") return;
    if (localStorage.getItem(DISMISS_KEY) === "1") return;
    if (!pushSupported() && !iosNeedsInstall()) return;
    if (typeof Notification !== "undefined" && Notification.permission === "denied") return;

    void (async () => {
      if (await hasActivePushSubscription()) return;
      setVisible(true);
    })();
  }, [session]);

  if (!visible) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  }

  async function enable() {
    setBusy(true);
    setError("");
    try {
      await enablePushNotifications();
      localStorage.setItem(DISMISS_KEY, "1");
      setVisible(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo activar push.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4 space-y-2">
      <Alert tone="review">
        {iosNeedsInstall() ? (
          <p className="text-sm">
            Para avisos de la portería en iPhone/iPad, tocá Compartir → Añadir a pantalla de
            inicio, abrí Zentrani desde el icono, y activá las notificaciones en{" "}
            <Link className="font-semibold underline" to="/ajustes">
              Ajustes
            </Link>
            .
          </p>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm">
              Activá notificaciones push para aprobar visitas de la portería al instante.
            </p>
            <div className="flex shrink-0 gap-2">
              <Button size="sm" loading={busy} onClick={() => void enable()}>
                Activar
              </Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={dismiss}>
                Ahora no
              </Button>
            </div>
          </div>
        )}
      </Alert>
      {error ? <Flash tone="stop">{error}</Flash> : null}
      {iosNeedsInstall() ? (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" onClick={dismiss}>
            Entendido
          </Button>
        </div>
      ) : null}
    </div>
  );
}
